// Setup › Sales tax on the SettingsPage pattern (BL-189): the tax zones (tax.listZones), each with its
// rates now and the ones that start later, and the zone an address with no zone of its own is charged
// (tax.defaultZoneId); then the tax numbers on every invoice (RULE_SECTIONS). As Setup.tsx's TaxTab draws
// them in Blueprint, with nothing changed: Add a tax zone and Add a rate open a FormSheet, a rate
// starts on a date and the one in force ends the day before; someone who can't change them reads them.
// While the company mirrors its import source the zones read, with the line (BL-088). Riverstone keeps
// TaxTab.
import { useCallback, useEffect, useState } from 'react';
import { Button, Checkbox, Choice, ChoiceGroup, Link, SelectField, TextField } from '@flowology/ui-web/fl';
import { FormSheet, SettingRow, SettingsPage, SettingsSection } from '@flowology/ui-web/patterns';
import { client } from '../api.ts';
import { PageBanners, usePageCrumb, type NavProps } from '../Nav.tsx';
import { refusal } from '../history/customer-edit-model.ts';
import { MIRROR_LINE, useMirror } from '../shared/mirror.ts';
import { useSaveKey } from '../shared/save-key.ts';
import { BP_LEDE, LEDE } from './ledes.ts';
import { settingsPage } from './parts.ts';
import { settingsRights } from './settings-model.ts';
import {
  componentInput,
  emptyRateForm,
  percentOf,
  ratesNow,
  setupRights,
  TAX_CLASSES,
  zoneInput,
  type RateForm,
  type Zone,
} from './setup-model.ts';
import { SetupRuleSections } from './SetupRules.tsx';

const CLASS_WORD: Record<string, string> = { standard: 'standard', labour: 'labour', materials: 'materials' };

/** Today in the company's zone, as the rates' dates are. */
const todayIn = (timeZone: string) =>
  new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(
    new Date(),
  );

export function TaxPage({ nav, timeZone }: { nav: NavProps; timeZone: string }) {
  const held = nav.permissions ?? [];
  const rights = settingsRights(held);
  const setup = setupRights(held);
  const mirroring = useMirror();
  const crumb = usePageCrumb(nav);
  return (
    <div className="app">
      {/* the sheet pads the page (blueprint.css .bp-sheet), as PageFrame does; no .page padding on top */}
      <main>
        <PageBanners nav={nav} />
        <SettingsPage
          head={{
            ...(crumb ? { crumb } : {}),
            title: settingsPage('tax').label ?? 'Sales tax',
            lede: BP_LEDE.tax ?? LEDE.tax,
          }}
        >
          <SetupRuleSections page="tax" top rights={rights} />
          <TaxZones
            canEdit={setup.org && !mirroring}
            mirrorLine={mirroring && setup.org}
            today={todayIn(timeZone)}
          />
          <SetupRuleSections page="tax" rights={rights} />
        </SettingsPage>
      </main>
    </div>
  );
}

type Open = { k: 'zone' } | { k: 'rate'; zone: Zone } | null;

function TaxZones({ canEdit, mirrorLine, today }: { canEdit: boolean; mirrorLine: boolean; today: string }) {
  const [zones, setZones] = useState<Zone[] | null>(null);
  const [def, setDef] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<Open>(null);
  const load = useCallback(() => {
    client
      .call<{ zones: Zone[]; defaultZoneId: string | null }>('tax.listZones', {})
      .then((r) => {
        setZones(r.zones);
        setDef(r.defaultZoneId);
        setError(null);
      })
      .catch((e: unknown) => setError(refusal(e)));
  }, []);
  useEffect(load, [load]);
  const notes = [mirrorLine && MIRROR_LINE, !canEdit && 'Only the owner or a manager changes these.'].filter(
    Boolean,
  );
  return (
    <>
      <SettingsSection
        title="Tax zones"
        {...(canEdit
          ? {
              action: (
                <Button variant="primary" onPress={() => setOpen({ k: 'zone' })}>
                  Add a tax zone
                </Button>
              ),
            }
          : {})}
        {...(notes.length ? { note: notes.join(' ') } : {})}
        status={zones ? (zones.length ? 'ready' : 'empty') : error ? 'failed' : 'loading'}
        emptyText="No tax zones yet. Add the province you work in, with its rates."
        {...(error ? { failedText: error } : {})}
        onRetry={load}
        foot="Rates already used on invoices stay as they were: a change starts on a date."
      >
        {zones?.map((z) => {
          const { now, later } = ratesNow(z, today);
          const tags = [z.id === def && 'Invoices use this', z.active === false && 'Retired in ServiceTitan'];
          const tag = tags.filter(Boolean).join(' · ');
          return (
            <SettingRow
              key={z.id}
              name={z.name}
              {...(tag ? { tag } : {})}
              help={
                <>
                  {now.map((c) => (
                    <span key={c.id}>
                      {c.code} {percentOf(c.rate)} on {c.appliesTo.map((a) => CLASS_WORD[a] ?? a).join(', ')}
                      <br />
                    </span>
                  ))}
                  {later.map((c) => (
                    <span key={c.id}>
                      {c.code} changes to {percentOf(c.rate)} on {c.effectiveFrom}
                      <br />
                    </span>
                  ))}
                </>
              }
              control={
                canEdit ? (
                  <Link label={`Add a rate to ${z.name}`} onPress={() => setOpen({ k: 'rate', zone: z })}>
                    Add a rate
                  </Link>
                ) : null
              }
            />
          );
        })}
        {/* which zone invoices use, chosen here (tax.defaultZoneId; the setup audit) */}
        {zones && zones.length > 0 && (
          <DefaultZone zones={zones} current={def} canEdit={canEdit} onSaved={load} />
        )}
      </SettingsSection>
      {/* beside the section, not in it: with no zones its body is the empty line, and a sheet in it never draws */}
      <ZoneSheet isOpen={open?.k === 'zone'} today={today} onSaved={load} onClose={() => setOpen(null)} />
      {open?.k === 'rate' && (
        <RateSheet
          key={open.zone.id}
          zone={open.zone}
          today={today}
          onSaved={load}
          onClose={() => setOpen(null)}
        />
      )}
    </>
  );
}

const DEFAULT_LABEL = 'An address with no zone of its own is charged';

function DefaultZone({
  zones,
  current,
  canEdit,
  onSaved,
}: {
  zones: readonly { id: string; name: string }[];
  current: string | null;
  canEdit: boolean;
  onSaved: () => void;
}) {
  const [pick, setPick] = useState(current ?? '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => setPick(current ?? ''), [current]);
  const name = zones.find((z) => z.id === current)?.name;
  if (!canEdit)
    return <SettingRow name={DEFAULT_LABEL} control={name ?? 'No sales tax until one is chosen'} />;
  const save = () => {
    setBusy(true);
    client
      .call('settings.set', { key: 'tax.defaultZoneId', value: pick })
      .then(() => {
        setError(null);
        onSaved();
      })
      .catch((e: unknown) => setError(refusal(e)))
      .finally(() => setBusy(false));
  };
  return (
    <>
      <SelectField
        label={DEFAULT_LABEL}
        value={pick}
        error={error}
        options={[
          ...(current ? [] : [{ value: '', label: 'Choose a zone' }]),
          ...zones.map((z) => ({ value: z.id, label: z.name })),
        ]}
        onChange={setPick}
      />
      {pick && pick !== current && (
        <Link isDisabled={busy} onPress={save}>
          Save
        </Link>
      )}
    </>
  );
}

/** A rate's fields: the tax (a new one typed, or one the zone has), its rate, the day it starts, and what it's on. */
function RateFields({
  r,
  onChange,
  codes,
}: {
  r: RateForm;
  onChange: (r: RateForm) => void;
  codes?: string[];
}) {
  return (
    <>
      {codes ? (
        <SelectField
          label="Tax"
          value={r.code}
          options={codes.map((c) => ({ value: c, label: c }))}
          onChange={(code) => onChange({ ...r, code })}
        />
      ) : (
        <TextField
          label="Tax"
          value={r.code}
          placeholder="GST"
          onChange={(code) => onChange({ ...r, code })}
        />
      )}
      <TextField
        label="Rate (%)"
        inputMode="decimal"
        value={r.percent}
        placeholder="5"
        onChange={(percent) => onChange({ ...r, percent })}
      />
      <TextField label="From" type="date" value={r.from} onChange={(from) => onChange({ ...r, from })} />
      <ChoiceGroup label="On" value={r.appliesTo} onChange={(appliesTo) => onChange({ ...r, appliesTo })}>
        {TAX_CLASSES.map(([k, label]) => (
          <Choice key={k} value={k}>
            {label}
          </Choice>
        ))}
      </ChoiceGroup>
    </>
  );
}

/** Adds a zone with its taxes (tax.createZone). */
function ZoneSheet({
  isOpen,
  today,
  onSaved,
  onClose,
}: {
  isOpen: boolean;
  today: string;
  onSaved: () => void;
  onClose: () => void;
}) {
  const [name, setName] = useState('');
  const [rates, setRates] = useState<RateForm[]>(() => [emptyRateForm(today)]);
  const save = useSaveKey(JSON.stringify([name, rates]));
  useEffect(() => {
    if (!isOpen) return;
    setName('');
    setRates([emptyRateForm(today)]);
  }, [isOpen]);
  const dirty = name !== '' || JSON.stringify(rates) !== JSON.stringify([emptyRateForm(today)]);
  return (
    <FormSheet
      isOpen={isOpen}
      onClose={onClose}
      title="Add a tax zone"
      submitLabel="Add zone"
      isDirty={dirty}
      isSubmitDisabled={!name.trim()}
      onSubmit={async () => {
        const built = zoneInput(name, rates);
        if ('error' in built) throw new Error(built.error);
        try {
          await client.call('tax.createZone', built.input, { idempotencyKey: save.key });
        } catch (e) {
          throw new Error(refusal(e), { cause: e });
        }
        save.renew();
        onSaved();
      }}
    >
      <TextField label="Zone" value={name} placeholder="British Columbia" autoFocus onChange={setName} />
      {rates.map((r, i) => (
        <RateBlock
          key={i}
          r={r}
          onChange={(x) => setRates(rates.map((y, j) => (j === i ? x : y)))}
          {...(rates.length > 1 ? { onRemove: () => setRates(rates.filter((_, j) => j !== i)) } : {})}
        />
      ))}
      <Link onPress={() => setRates([...rates, { ...emptyRateForm(today), appliesTo: ['materials'] }])}>
        Add another tax
      </Link>
    </FormSheet>
  );
}

function RateBlock({
  r,
  onChange,
  onRemove,
}: {
  r: RateForm;
  onChange: (r: RateForm) => void;
  onRemove?: () => void;
}) {
  return (
    <>
      <RateFields r={r} onChange={onChange} />
      {onRemove && <Link onPress={onRemove}>Remove this tax</Link>}
    </>
  );
}

/** Adds a rate to a zone (tax.addRate): the one in force ends the day before it starts. */
function RateSheet({
  zone,
  today,
  onSaved,
  onClose,
}: {
  zone: Zone;
  today: string;
  onSaved: () => void;
  onClose: () => void;
}) {
  const codes = [...new Set(zone.components.map((c) => c.code))];
  const [first] = useState<RateForm>(() => {
    const cur = ratesNow(zone, today).now[0];
    return { ...emptyRateForm(today), code: codes[0] ?? '', appliesTo: cur?.appliesTo ?? ['standard'] };
  });
  const [r, setR] = useState<RateForm>(first);
  const [isNew, setIsNew] = useState(!codes.length);
  const save = useSaveKey(JSON.stringify(r));
  return (
    <FormSheet
      isOpen
      onClose={onClose}
      title={`A new rate in ${zone.name}`}
      submitLabel="Add rate"
      note="The rate in force now ends the day before this one starts."
      isDirty={JSON.stringify(r) !== JSON.stringify(first)}
      onSubmit={async () => {
        const c = componentInput(r);
        if ('error' in c) throw new Error(c.error);
        try {
          await client.call(
            'tax.addRate',
            { taxZoneId: zone.id, component: c.component },
            { idempotencyKey: save.key },
          );
        } catch (e) {
          throw new Error(refusal(e), { cause: e });
        }
        save.renew();
        onSaved();
      }}
    >
      {codes.length > 0 && (
        <Checkbox
          isSelected={isNew}
          onChange={(on) => {
            setIsNew(on);
            setR({ ...r, code: on ? '' : codes[0]! });
          }}
        >
          A tax this zone doesn’t have yet
        </Checkbox>
      )}
      <RateFields r={r} onChange={setR} {...(isNew ? {} : { codes })} />
    </FormSheet>
  );
}
